import { useEffect, useId, useMemo, useRef, useState, type CSSProperties } from 'react';
import { IconClock } from '@tabler/icons-react';
import { Popover } from './Popover.js';
import { Pressable } from './Pressable.js';
import { cx } from '../utils/cx.js';
import styles from './TimePicker.module.css';

export type TimePickerProps = {
  /** 24h wall clock `HH:mm`. */
  value?: string;
  onChange: (nextHm: string) => void;
  label?: string;
  hint?: string;
  error?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  placeholder?: string;
  style?: CSSProperties;
  ariaLabel?: string;
  /** Minute step (default 1). */
  minuteStep?: number;
};

function parseHm(value?: string): { hour: number; minute: number } | null {
  if (!value || !/^\d{1,2}:\d{2}$/.test(value.trim())) return null;
  const [hRaw, mRaw] = value.trim().split(':');
  const hour = Number.parseInt(hRaw ?? '', 10);
  const minute = Number.parseInt(mRaw ?? '', 10);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return { hour, minute };
}

function formatHm(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function buildMinutes(step: number): number[] {
  const s = Math.max(1, Math.min(30, Math.floor(step)));
  const out: number[] = [];
  for (let m = 0; m < 60; m += s) out.push(m);
  return out;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);

export function TimePicker({
  value,
  onChange,
  label,
  hint,
  error,
  id,
  name,
  disabled,
  required,
  className,
  placeholder = 'HH:mm',
  style,
  ariaLabel,
  minuteStep = 1,
}: TimePickerProps) {
  const autoId = useId();
  const triggerId = id ?? name ?? autoId;
  const hiddenId = `${triggerId}-value`;
  const errorId = `${triggerId}-error`;
  const parsed = useMemo(() => parseHm(value), [value]);
  const minutes = useMemo(() => buildMinutes(minuteStep), [minuteStep]);
  const [open, setOpen] = useState(false);
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);

  const displayValue = parsed ? formatHm(parsed.hour, parsed.minute) : '';

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      hourListRef.current
        ?.querySelector<HTMLElement>('[data-selected="true"]')
        ?.scrollIntoView({ block: 'center' });
      minuteListRef.current
        ?.querySelector<HTMLElement>('[data-selected="true"]')
        ?.scrollIntoView({ block: 'center' });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, parsed?.hour, parsed?.minute]);

  const pick = (hour: number, minute: number) => {
    onChange(formatHm(hour, minute));
  };

  return (
    <div className={cx(styles.wrap, className)}>
      {label ? (
        <label className={styles.label} htmlFor={triggerId}>
          {label}
        </label>
      ) : null}

      <input type="hidden" id={hiddenId} name={name} value={value ?? ''} required={required} />

      <Popover
        opened={open}
        onChange={setOpen}
        portal
        modal
        align="start"
        portalMinWidth={176}
        target={
          <Pressable
            id={triggerId}
            type="button"
            className={cx(styles.trigger, error && styles.error, disabled && styles.disabled)}
            aria-label={ariaLabel ?? label}
            aria-haspopup="dialog"
            aria-invalid={!!error}
            aria-required={required || undefined}
            aria-describedby={error ? errorId : undefined}
            disabled={disabled}
            style={style}
          >
            <span className={cx(styles.triggerValue, !displayValue && styles.placeholder)}>
              {displayValue || placeholder}
            </span>
            <IconClock size={16} stroke={1.8} className={styles.clockIcon} aria-hidden />
          </Pressable>
        }
      >
        <div className={styles.panel} role="dialog" aria-label="Choisir une heure">
          <div className={styles.columns}>
            <div className={styles.column}>
              <span className={styles.columnLabel}>Heure</span>
              <div ref={hourListRef} className={styles.list} role="listbox" aria-label="Heures">
                {HOURS.map((hour) => {
                  const selected = parsed?.hour === hour;
                  return (
                    <Pressable
                      key={hour}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      data-selected={selected ? 'true' : undefined}
                      className={cx(styles.cell, selected && styles.cellSelected)}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        pick(hour, parsed?.minute ?? 0);
                      }}
                    >
                      {String(hour).padStart(2, '0')}
                    </Pressable>
                  );
                })}
              </div>
            </div>
            <div className={styles.column}>
              <span className={styles.columnLabel}>Min</span>
              <div ref={minuteListRef} className={styles.list} role="listbox" aria-label="Minutes">
                {minutes.map((minute) => {
                  const selected = parsed?.minute === minute;
                  return (
                    <Pressable
                      key={minute}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      data-selected={selected ? 'true' : undefined}
                      className={cx(styles.cell, selected && styles.cellSelected)}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        pick(parsed?.hour ?? 0, minute);
                        setOpen(false);
                      }}
                    >
                      {String(minute).padStart(2, '0')}
                    </Pressable>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Popover>

      {hint && !error ? <p className={styles.hint}>{hint}</p> : null}
      {error ? (
        <p id={errorId} className={styles.errText} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
